'use client'
import { Form, FormField } from '@/components/ui/form/form'
import { useForm } from 'react-hook-form'
import { FormMode } from '@/lib/common-models'
import {
  RadioButtonCard,
  RadioButtonRawCardInput,
  RadioIcon,
} from '@/components/ui/fields/radio-button-raw-card-input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog/dialog'
import FormDebugger from '@/components/form/form-debugger/form-debugger'
import xpressFormImage from '@/public/image/icons/xpress-form-icon.svg'
import Image from 'next/image'
import { BookText, ChevronRight } from 'lucide-react'
import { Schema } from '@/lib/fetching/schema'
import { Button } from '@/components/ui/button/button'
import { setTrackerCookie } from '@/lib/common-utils'
import { logger } from '@/utils/logger'

interface WowJourneyDialogProps {
  schema: Schema
}

// TO-DO: Add Support to Link to Wow Journey when journey arrives
const ImageIcon: React.FC<{
  src?: string
  alt: string
  width?: number
  className?: string
}> = ({ src, alt, width, className }) => {
  return src ? (
    <Image
      src={`data:image/svg+xml;base64,${src}`}
      alt={alt}
      width={width ?? 30}
      height={30}
      className={className}
    />
  ) : (
    <BookText name="image" width={30} height={30} />
  )
}

export const WowJourneyDialog = ({ schema }: WowJourneyDialogProps) => {
  const form = useForm()
  const schemePreference = form.watch('schemePreference')

  const handleWowJourneyClick = () => {
    const selectedJourney = form.getValues('schemePreference')
    const filterSelectedJourney = schema?.wowJourney?.find(
      (journey) => journey?.title === selectedJourney
    )
    const redirectUrl = filterSelectedJourney?.link ?? ''
    const formId = schema?.id
    const wowJourneyRedirect = filterSelectedJourney?.title

    if (selectedJourney === 'xpress-forms') {
      try {
        setTrackerCookie(formId, 10)
      } catch (err) {
        logger.error({ message: 'Failed to set tracker ID', error: err })
      }
    }

    const targetUrl =
      selectedJourney === wowJourneyRedirect ? redirectUrl : `forms/${formId}`
    globalThis.location.href = targetUrl
  }

  return (
    <>
      <FormDebugger control={form.control} />
      <Form {...form} key={schema.id}>
        <Dialog>
          <DialogTrigger asChild className="pr-2">
            <div className="flex justify-between">
              <Button
                variant="secondary"
                className="flex items-center justify-between rounded-lg bg-white p-2 text-left font-semibold hover:bg-transparent"
                transactionName="forms.wow-journey-dialog"
              >
                <div className="flex min-h-[32px] gap-1">
                  <div className="mr-2 shrink-0">
                    <ImageIcon src={schema?.img?.data} alt={schema?.name} />
                  </div>
                  <div className="grow">
                    <p className="whitespace-normal text-[18px] font-semibold leading-7">
                      {schema.name}
                    </p>
                  </div>
                </div>
              </Button>
              <ChevronRight className="size-6 shrink-0 text-blue-700 md:size-8" />
            </div>
          </DialogTrigger>
          <DialogContent className="w-full !max-w-[820px]">
            <DialogHeader>
              <DialogTitle className="capitalize">{schema.name}</DialogTitle>
              <p className="text-center text-sm font-normal text-[#4B5563]">
                Instant Online Service Journey already exists for this
                service/instruction.
              </p>
            </DialogHeader>
            <div className="w-full p-2 pt-4">
              <FormField
                name="schemePreference"
                defaultValue="insta-services"
                render={({ field }) => (
                  <div className="w-full p-2 pt-4">
                    <RadioButtonRawCardInput
                      mode={FormMode.CREATE}
                      field={field}
                    >
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {schema?.wowJourney?.map((journey) => (
                          <div
                            key={journey?.title}
                            className="relative w-full max-w-[368px]"
                          >
                            {journey?.recommended && (
                              <div className="absolute bottom-[116px] left-8 flex h-6 w-32 items-center justify-center rounded-xl bg-[#00875F] text-center text-[14px] text-white">
                                <p>Recommended!</p>
                              </div>
                            )}
                            <RadioButtonCard value={journey?.title}>
                              <div className="flex h-32 px-2 py-6">
                                <div className="flex gap-3">
                                  <div className="mt-0 w-full max-w-14">
                                    <ImageIcon
                                      src={journey?.icon?.data}
                                      alt={journey?.title}
                                      width={44}
                                      className="max-w-14"
                                    />
                                  </div>
                                  <div>
                                    <p className="text-[16px] font-semibold text-[#111827]">
                                      {journey?.title}
                                    </p>
                                    <p className="text-xs font-normal leading-5 text-[#4B5563]">
                                      {journey?.description}
                                    </p>
                                  </div>
                                </div>
                                <div>
                                  <RadioIcon />
                                </div>
                              </div>
                            </RadioButtonCard>
                          </div>
                        ))}

                        <div className="w-full max-w-[368px]">
                          <RadioButtonCard value="xpress-forms">
                            <div className="flex h-32 px-2 py-6">
                              <div className="flex gap-3">
                                <div className="mt-0 w-full max-w-14">
                                  <Image
                                    src={xpressFormImage}
                                    alt="xpress-form-icons"
                                    width={44}
                                    height={30}
                                  />
                                </div>
                                <div>
                                  <p className="text-[16px] font-semibold text-[#111827]">
                                    Xpress Forms
                                  </p>
                                  <p className="text-xs font-normal leading-5 text-[#4B5563]">
                                    Processed at branch with multiple
                                    authorizations
                                  </p>
                                </div>
                              </div>
                              <div>
                                <RadioIcon />
                              </div>
                            </div>
                          </RadioButtonCard>
                        </div>
                      </div>
                    </RadioButtonRawCardInput>
                  </div>
                )}
              />
            </div>
            <div className="mt-10 flex w-full justify-center">
              <Button
                className="m-0 flex h-14 w-1/2 items-center gap-2 rounded-xl"
                onClick={handleWowJourneyClick}
                disabled={!schemePreference}
                transactionName="forms.wow-journey-dialog.continue"
              >
                Continue
                <ChevronRight />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </Form>
    </>
  )
}
